import type { ClientFormTag, DateInterval, Translations, UserWithTags } from '@pbk-web/types';

import type { TrainingAccessGroupResponse, TrainingAccessRoleGroupRequest } from './TrainingAssignment';
import type { TrainingEventUserStatus } from './TrainingEventUser';

export type { ClientFormTag, UserWithTags } from '@pbk-web/types';

export type Training = {
  id: string;
  title: string;
  clientId: string;
  translations: Translations;
  status: TrainingStatus;
  statusUtc: string;
  useUniqueIdentifier: boolean;
  createdUtc: string;
  createdByName: string;
  defaultUsers: TrainingMemberResponse[];
  defaultUserGroups: TrainingUserGroupResponse[];
  originId?: string;
  defaultClientFormIds?: string[];
  eventsCount?: number;
  validForAmount?: number;
  validForInterval?: DateInterval;
};

export type TrainingCreate = {
  clientId: string;
  title: string;
  translations: Translations;
  useUniqueIdentifier: boolean;
  originId?: string;
  /** Policy-based member assignment (Trainer / Trainee categories). Full-replace on save. */
  access?: TrainingAccessRoleGroupRequest[];
  validForAmount?: number;
  validForInterval?: DateInterval;
};

export enum TrainingStatus {
  Draft = 0,
  Active = 1,
}

export enum TrainingMemberType {
  Trainee = 0,
  Trainer = 1,
}

/** Stable, copy-independent slugs for each member category — used as `data-category` on the category rows
 *  so automated tests can target a row without matching its translated label. */
export const TrainingMemberTypeSlugs = {
  [TrainingMemberType.Trainee]: 'trainee',
  [TrainingMemberType.Trainer]: 'trainer',
} as const;

export type TrainingUserRequest = {
  memberType: TrainingMemberType;
  userId: string;
  tagIds: string[];
  addedAsIndividual: boolean;
};

export type TrainingTagRequest = {
  id: string;
  synchronized: boolean;
};

export type TrainingMemberRequest = {
  memberType: TrainingMemberType;
  users: TrainingUserRequest[];
  tags: TrainingTagRequest[];
};

export type TrainingMemberResponse = {
  memberType: TrainingMemberType;
  tagIds: string[];
  user: UserWithTags;
  userId?: string;
  score?: number;
  status?: TrainingEventUserStatus;
  statusUtc?: string;
  latestAttemptNumber?: number;
  addedAsIndividual?: boolean;
};

export type TrainingTag = ClientFormTag & {
  // Set by FE
  memberType?: number;
};

export type TrainingUserGroupResponse = {
  memberType: TrainingMemberType;
  synchronized: boolean;
  tag: TrainingTag;
};

export type ClientTrainingEventUsersResponse = {
  programUserIds: string[];
  userIdsByFormId: Record<string, string[]>;
};

export type TrainingRequirementsResponse = {
  coverageThreshold: number;
  validityAmount: number;
  validityInterval: DateInterval;
  /** Policy-based required trainees (Trainee category) + server-resolved members. Folded group shape
   *  (`categories` keyed by `type`), distinct from the program/event access `members`/`role` shape. */
  access?: TrainingAccessGroupResponse;
};

export type UpsertTrainingRequirementsRequest = {
  coverageThreshold: number;
  validityAmount: number;
  validityInterval: DateInterval;
  /** Policy-based required trainees (single Trainee category). Full-replace on save. */
  access?: TrainingAccessRoleGroupRequest[];
};

export type TrainingCoverageResponse = {
  completionPercentage: number;
  completedCount: number;
  totalCount: number;
  effectiveDateUtc: string | null;
};

export type TrainingStats = {
  totalEvents: number;
  upcomingEvents: number;
  inProgressEvents: number;
  completedEvents: number;
  blockedEvents: number;
  notStartedEvents: number;
  // Trainee stats
  passedTrainees: number;
  failedTrainees: number;
  overDueTrainees: number;
  totalTrainees: number;
  completedTrainees: number;
  inProgressTrainees: number;
  notStartedTrainees: number;
  overdueTrainees: number;
  averageScoreTrainees: number;
};
